/** SUNTRONIX ONE — Customer Hub → Our Feedback. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Smile, Star } from "lucide-react";

const RATING = [[5, "Very Good"], [4, "Good"], [3, "Average"], [2, "Poor"], [1, "Very Poor"]];
const RTINT = { "VERY GOOD": "bg-emerald-100 text-emerald-800", GOOD: "bg-teal-100 text-teal-800",
  AVERAGE: "bg-amber-100 text-amber-800", POOR: "bg-orange-100 text-orange-800", "VERY POOR": "bg-rose-100 text-rose-800" };

export default function OurFeedback() {
  const [data, setData] = useState(null);
  const [respondFor, setRespondFor] = useState(null);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");

  const load = useCallback(() => {
    api.get("/csat/customer/feedback").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!rating) return;
    try {
      await api.post(`/csat/requests/${respondFor.id}/respond`, { rating, comment: comment || null });
      toast.success("Thank you for your feedback!");
      setRespondFor(null); setRating(null); setComment(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mx-auto max-w-[1000px] space-y-4" data-testid="our-feedback-page">
      <header className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-teal-900 text-white"><Smile className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">Our Feedback</div>
          <div className="text-[11px] text-slate-500">Share your experience and see feedback you've submitted</div>
        </div>
        {data?.csat != null && (
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center" data-testid="our-feedback-csat">
            <div className="text-lg font-semibold text-emerald-700">{data.csat}%</div>
            <div className="text-[9px] uppercase text-slate-400">Our CSAT</div>
          </div>
        )}
      </header>

      {(data?.pending || []).length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="our-feedback-pending">
          <div className="mb-2 text-[12px] font-semibold text-amber-800">Pending feedback ({data.pending.length})</div>
          {data.pending.map((r) => (
            <div key={r.id} className="flex items-center gap-2 border-t border-amber-100 py-1.5 text-[11px]">
              <span className="rounded bg-white px-1.5 py-0.5 text-[9px] font-semibold">{r.source_type}</span>
              <span>{r.service_type}</span><span className="text-slate-400">{r.reference}</span>
              <button onClick={() => setRespondFor(r)} data-testid={`our-feedback-give-${r.id}`}
                className="ml-auto rounded-md bg-teal-800 px-2.5 py-1 text-[10px] font-semibold text-white">Give feedback</button>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="our-feedback-history">
        <div className="border-b border-slate-100 px-3 py-2 text-[12px] font-semibold">History ({data?.total || 0})</div>
        {(data?.items || []).map((r) => (
          <div key={r.id} className="flex items-center gap-2 border-t border-slate-100 px-3 py-1.5 text-[11px]">
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold">{r.source_type}</span>
            <span>{r.service_type}</span>
            <span className="ml-auto">{r.rating_label ? <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${RTINT[r.rating_label]}`}>{r.rating_label}</span> : <span className="text-slate-400">Awaiting</span>}</span>
          </div>
        ))}
        {!(data?.items || []).length && <div className="px-3 py-8 text-center text-[12px] text-slate-400">No feedback yet.</div>}
      </div>

      {respondFor && (
        <div className="fixed inset-0 z-[120] flex items-start justify-center bg-slate-900/40 p-4 pt-16" onClick={() => setRespondFor(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="our-feedback-dialog">
            <div className="text-[14px] font-semibold text-slate-900">How was your experience?</div>
            <div className="text-[11px] text-slate-500">{respondFor.service_type} {respondFor.reference ? `· ${respondFor.reference}` : ""}</div>
            <div className="mt-3 space-y-1.5">
              {RATING.map(([v, l]) => (
                <button key={v} onClick={() => setRating(v)} data-testid={`our-feedback-rate-${v}`}
                  className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-[12px] font-medium ${rating === v ? "border-teal-600 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-700"}`}>
                  <span>{["😡", "🙁", "😐", "🙂", "😃"][v - 1]}</span> {l}
                </button>
              ))}
            </div>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Comment (optional)"
              className="mt-2 w-full rounded-md border border-slate-200 px-2 py-1.5 text-[12px]" />
            <button onClick={submit} disabled={!rating} data-testid="our-feedback-submit"
              className="mt-3 w-full rounded-lg bg-teal-800 py-2 text-[12px] font-semibold text-white disabled:opacity-40">Submit</button>
          </div>
        </div>
      )}
    </div>
  );
}
